import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import axios from "axios";
import _ from "underscore";
import botAction from "./modules/botAction";
import { STEP_STATUS } from "./modules/botAction";
import operatorAction from "./modules/operatorAction";
import search from "./modules/search";
import messagesHandler from "./modules/messagesHandler";
import ticket from "./modules/ticket";
import enquete from "./modules/enquete";
import commonFunctionConst from "@/const/function";
import commonMessageConst from "@/const/message";
import productsFunctionConst from "@product/const/function";
import productsMessageConst from "@product/const/message";
import { generateRandomQuery } from "@/libs/query-utils";

Vue.use(Vuex);

function getRoleFileUrl(originalUrl, role) {
  if (originalUrl.endsWith('_test.json')) {
    return originalUrl.replace('_test.json', `_${role}_test.json`);
  } else if (originalUrl.endsWith('.json')) {
    return originalUrl.replace('.json', `_${role}.json`);
  } else {
    return originalUrl;
  }
}

function getRoleResourceUrl() {
  const { productId, nodeEnv } = ENV_SETTINGS;
  switch (nodeEnv) {
    case "prod":
      return `https://service.ai-x-supporter.com/${productId}/bot_package.json`;
    case "test":
      return `https://service.ai-x-supporter-dev.com/${productId}/bot_package_test.json`;
    case "dev":
    default:
      return `https://service.ai-x-supporter-dev.com/${productId}/bot_package.json`;
  }
}

export default new Vuex.Store({
  plugins: [
    createPersistedState({
      storage: window.sessionStorage,
      key: "sAIChatWindow",
      paths: [
        'botAction', 
        'enquete', 
        'messagesHandler', 
        'operatorAction', 
        'search', 
        'ticket'
      ]
    })
  ],
  state: {
    resources: undefined,
    searchingMessage: "",
    constObj: undefined,
    category: undefined
  },
  getters: {
    isInitialState(state) {
      return state.messagesHandler.messages.length == 0;
    }
  },
  mutations: {
    setResources(state, payload) {
      state.resources = payload.resources;
    },
    setConstObj(state, payload) {
      state.constObj = payload.constObj;
    },
    setCategory(state, payload) {
      state.category = payload.category;
    },
    setDefaultMessage(state, payload) {
      state.defaultMessage = payload.defaultMessage;
    }
  },
  actions: {
    async initChatWindow({ commit, state, dispatch }, constObj) {
      console.log("v3.0");
      const obj = Object.assign(
        commonFunctionConst,
        commonMessageConst,
        productsFunctionConst,
        productsMessageConst,
        state.defaultMessage
      );
      commit("setConstObj", { constObj: obj });

      // チャットボットは外部サイトにscriptタグでapp.bundle.jsを埋め込むため、URLのクエリパラメータは取得できない。
      // そのため、windowオブジェクトにai_app_script_queryというプロパティを設定し、そこにroleを設定する。
      const aiAppScriptQuery = window.ai_app_script_query || {};
      const role = aiAppScriptQuery.role;

      if (state.constObj.IS_ROLE_RESOURCE_FILE && role) {
        await dispatch("fetchRoleCategory", role);
        await dispatch("fetchRoleResource", role);
      } else {
        await dispatch("fetchCategory");
        await dispatch("fetchResource");
      }
      await dispatch("fetchDefaultMessage");

      commit("setConstObj", { constObj: Object.assign(
        commonFunctionConst,
        commonMessageConst,
        productsFunctionConst,
        productsMessageConst,
        state.defaultMessage
      )});

      if (this.getters.isInitialState) {
        dispatch("dispatchAction");
      }
      if (obj.FORCE_RESET) {
        commit("clearMessages");
        dispatch("addInitMessage");
      }
    },

    async fetchResource({ commit }) {
      const resourcePath = `${ENV_SETTINGS.resourceFileUrl}?v=${generateRandomQuery()}`;
      try {
        const result = await axios.get(resourcePath);
        commit("setResources", { resources: result.data });
      } catch (e) {
        commit("setResources", { resources: null });
        console.error(e.message || 'Fetch resource failed.');
      }
    },

    async fetchRoleResource({ commit }, role) {
      const url = getRoleResourceUrl();
      const resourceFileUrl = getRoleFileUrl(url, role)
      const resourcePath = `${resourceFileUrl}?v=${generateRandomQuery()}`;
      try {
        const { data: resources } = await axios.get(resourcePath);
        commit("setResources", { resources: resources });
      } catch (e) {
        commit("setResources", { resources: null });
        console.error(e.message || 'Fetch role resource failed.');
      }
    },

    async fetchDefaultMessage({ commit }) {
      const defaultMessagePath = `${ENV_SETTINGS.defaultMessageUrl}?v=${generateRandomQuery()}`;
      try {
        const result = await axios.get(defaultMessagePath);
        commit("setDefaultMessage", { defaultMessage: result.data });
      } catch (e) {
        console.log("default message not found");
      }
    },

    async fetchCategory({ commit }) {
      const resourcePath = `${ENV_SETTINGS.categoryFileUrl}?v=${generateRandomQuery()}`;
      try {
        const result = await axios.get(resourcePath);
        commit("setCategory", { category: result.data });
      } catch (e) {
        commit("setCategory", { category: null });
        console.error(e.message || 'Fetch category failed.');
      }
    },

    async fetchRoleCategory({ commit }, role) {
      try {
        const url = getRoleFileUrl(ENV_SETTINGS.categoryFileUrl, role)
        const { data } = await axios.get(`${url}?v=${generateRandomQuery()}`);
        commit("setCategory", { category: data });
      } catch (e) {
        commit("setCategory", { category: null });
        console.error(e.message || 'Fetch role category failed.');
      }
    },

    async distributeChoiceMessage(
      { commit, state, rootState, dispatch },
      answer
    ) {
      commit("addMessage", {
        from: "user",
        content: { text: answer.choice }
      });


      if (state.operatorAction.connectStatus === "connected") {
        switch (answer.keyName) {
          case "complete":
            dispatch("sendFeedbackToServer", answer);
            commit("addMessage", {
              from: "bot",
              content: { text: state.constObj.OPERATOR_FEEDBACK_DONE_MESSAGE }
            });
            break;
          case "unsolved":
            dispatch("sendFeedbackToServer", answer);
            commit("addMessage", {
              from: "bot",
              content: { text: state.constObj.OPERATOR_FEEDBACK_ERROR_MESSAGE }
            });
            break;
        }
      } else {
        switch (answer.keyName) {
          case "escalated":
            await dispatch("postTicket", {
              mode: "bot",
              status: "escalated"
            });
            await dispatch("initConnection");
            break;
          case "request_connect":
            dispatch("initConnection");
            break;
          case "request_unconnect":
            dispatch("abortConnection");
            break;
          case "complete":
            dispatch("dispatchAction", answer.keyName);
            if (state.constObj.HAS_RESOLVED_ENQUETE) {
              dispatch("openEnquete", "bot");
            }
            break;
          default:
            dispatch("dispatchAction", answer.keyName);
        }
      }
    },

    async distributeChoiceMessageForBackStep(
      { commit, rootState, state, dispatch },
      { answer, selectedIndex }
    ) {
      if (selectedIndex > 0) {
        /**
         * 子カテゴリ以降が選択された時
         * 1. 選択された質問の1つ前までを削除して詰め直す（選択された質問の1つ前のstepStatusに戻す）
         * 2. 選択された質問を追加する
         */
        commit("subMessages", {
          lastIndex: selectedIndex + 1,
        });
        const excludeMessage = [
          rootState.constObj.BACK_TO_SCENARIO_MESSAGE,
          rootState.constObj.FEEDBACK_DONE_MESSAGE,
          rootState.constObj.FEEDBACK_ERROR_MESSAGE
        ]
        const botMessages = state.messagesHandler.messages.filter(
          function(m, index, array) {
            if (m.from !== "bot") {
              return false;
            }

            if (rootState.constObj.ASK_FEEDBACK_MESSAGE == m.content.text)
            {
              return Object.values(rootState.constObj.ASK_FEEDBACK_CHOICES).includes(state.messagesHandler.messages[index+1].content.text)
            }

            if (rootState.constObj.INTERRUPTION_MESSAGE == m.content.text)
            {
              return Object.values(rootState.constObj.INTERRUPTION_CHOICES).includes(state.messagesHandler.messages[index+1].content.text)
            }

            return !excludeMessage.includes(m.content.text);
           }
        );

        const lastIndex = _.findLastIndex(
          botMessages,
          m => {
            if (!Array.isArray(m.content.choices)) {
              // 検索時は choices がオブジェクトになる
              return m.content.choices[answer.keyName] || m.content.choices[answer.choice];
            }
            return m.content.choices.some(val => val.includes(answer.choice));
          }
        );

        const prevStepId = state.messagesHandler.stepIdHistory[lastIndex-1];

        commit("subStepIdHistory", {
          lastIndex: lastIndex,
        });

        commit("addMessage", {
          from: "user",
          content: { text: answer.choice }
        });

        await dispatch("backStep", {
          stepId: prevStepId,
        });
      } else {
        /**
         * カテゴリ（最初の質問）が選択された時
         * 1. messagesをリセットする
         * 2. initMessageを追加する
         */
        commit("clearMessages");
        dispatch("addInitMessage", answer.keyName);
        commit("addMessage", {
          from: "user",
          content: { text: answer.choice }
        });
      }
      
      dispatch("backStepAction", {
        keyName: answer.keyName,
        index: selectedIndex,
      });
    },

    async distributeTextMessge({ commit, state, rootState, dispatch }, text) {
      const lastIndex = _.findLastIndex(
        state.messagesHandler.messages,
        m => m.from != "user"
      );
      if (!rootState.constObj.ENABLE_BACK_STEP || (state.botAction.stepStatus === STEP_STATUS.FEEDBACK || state.botAction.stepStatus === STEP_STATUS.INTERRUPTION )) {
        commit("disabledChoices", { index: lastIndex });
      }
      commit("addMessage", { from: "user", content: { text: text } });
      switch (rootState.operatorAction.connectStatus) {
        case "request":
          const cancellState = { text: text, status: "request" };
          dispatch("cancellMessageToServer", cancellState);
          break;
        case "connected":
          dispatch("sendMessageToServer", { text: text });
          break;
        default:
          const isGreeting = await dispatch("isGreetingMessages", text);
          if (isGreeting && rootState.botAction.stepId === "init") {
            const answer = await dispatch("searchGreetingMessage", text);
            const greetingMessage = {
              from: "bot",
              content: { text: answer },
              status: "init"
            };
            await commit("addMessage", greetingMessage);
            return;
          }
          dispatch("searchMessage", text);
      }
    },

    resetMessages({ commit, state, rootState, dispatch }) {
      if (rootState.operatorAction.connectStatus != "unconnected") {
        dispatch("disConnection");
      }
      const mode =
        rootState.operatorAction.connectStatus != "unconnected"
          ? "operator"
          : "bot";
      dispatch("postTicket", { status: "quit", mode: mode });
      commit("clearMessages");
      dispatch("addInitMessage");
    }
  },
  modules: {
    botAction: botAction,
    search: search,
    operatorAction: operatorAction,
    messagesHandler: messagesHandler,
    ticket: ticket,
    enquete: enquete
  }
});
